// Switches (design 9.6). They live in the D1 settings table, override the
// Worker's environment values in both directions, and flip from the admin
// without a deploy. New features ship dark: every feature switch defaults OFF.
//
// Precedence when resolving a switch: D1 setting, then env var, then default.
// A value that does not parse is ignored with an issue reported (so a typo
// can never silently turn a feature on).
import { z } from "zod";

const Share = z.number().min(0).max(1);

/**
 * The value schema of every switch, WITHOUT its default. Patch requests are
 * built from these, so a patch that names one switch never fills the others
 * with their defaults (the zod 4 `.partial()` of a schema with defaults does,
 * VERIFIED Sept 28 2026; found by the admin engineer).
 */
export const SwitchValues = {
  CONSULTS_ENABLED: z.boolean(),
  DRAIN: z.boolean(),
  ROUTE_TO_LEGACY: z.boolean(),
  HANDOFF_ENABLED: z.boolean(),
  CRM_ENABLED: z.boolean(),
  SLACK_ENABLED: z.boolean(),
  COACH_ENABLED: z.boolean(),
  /** The minute 7 checkpoint (design 4.5, a stretch item in 12.2). Needs the coach's drafter. */
  CHECKPOINT_ENABLED: z.boolean(),
  DRAFTER_MODEL: z.string().min(1),
  WARM_START_ENABLED: z.boolean(),
  SPANISH_ENABLED: z.boolean(),
  VIDEO_MODE_ENABLED: z.boolean(),
  VOICE_MODE_ENABLED: z.boolean(),
  CHANNELS_ACTIVE: z.record(z.string(), z.boolean()),
  VOICE_MODEL: z.string().min(1),
  FRESH_SESSION_MINUTES: z.number().int().min(0).max(10),
  MIC_GATE_MODE: z.enum(["attenuate", "silence", "drop"]),
  VAD_PROFILE: z.enum(["home", "field", "room"]),
  DUCK_ENABLED: z.boolean(),
  EVENT_MODE: z.boolean(),
  HYBRID_VAD_SHARE: Share,
  JUDGE_SAMPLE_RATE: Share,
  RESUME_EMAIL_ENABLED: z.boolean(),
  VOICE_ENGINE: z.enum(["live", "cascade"]),
  CASCADE_SHARE: Share,
} as const;

export const Switches = z.object({
  /** Master switch for new check-ins. Off until Wes opens the public link. */
  CONSULTS_ENABLED: SwitchValues.CONSULTS_ENABLED.default(false),
  /** No new consults; the check-in says Otto is between calls. Used before an otto-live deploy. */
  DRAIN: SwitchValues.DRAIN.default(false),
  /** Send the v2 check-in to the old consult (rollback). */
  ROUTE_TO_LEGACY: SwitchValues.ROUTE_TO_LEGACY.default(false),
  /** POST each package to the configured webhook. OFF by default (Wes, Sept 28 2026). */
  HANDOFF_ENABLED: SwitchValues.HANDOFF_ENABLED.default(false),
  /** Close CRM writes. Not in this phase; stays off. */
  CRM_ENABLED: SwitchValues.CRM_ENABLED.default(false),
  SLACK_ENABLED: SwitchValues.SLACK_ENABLED.default(false),
  COACH_ENABLED: SwitchValues.COACH_ENABLED.default(false),
  /** Off by default: the checkpoint ships dark, separately from the coach. */
  CHECKPOINT_ENABLED: SwitchValues.CHECKPOINT_ENABLED.default(false),
  DRAFTER_MODEL: SwitchValues.DRAFTER_MODEL.default("gemini-3.8-flash"),
  WARM_START_ENABLED: SwitchValues.WARM_START_ENABLED.default(false),
  SPANISH_ENABLED: SwitchValues.SPANISH_ENABLED.default(false),
  VIDEO_MODE_ENABLED: SwitchValues.VIDEO_MODE_ENABLED.default(true),
  VOICE_MODE_ENABLED: SwitchValues.VOICE_MODE_ENABLED.default(true),
  /** One ACTIVE switch per channel (design 9.6). Channels are a later phase; general is on. */
  CHANNELS_ACTIVE: SwitchValues.CHANNELS_ACTIVE.default({ general: true }),
  VOICE_MODEL: SwitchValues.VOICE_MODEL.default("gemini-3.1-flash-live-preview"),
  /**
   * 0 = compression mode (the safe fallback); 4 = rolling sessions, only after
   * tests T4 and T9 pass (design 4.2). Stays 0 by default: switching the
   * launch default is Wes's decision (integration note, Sept 28 2026).
   */
  FRESH_SESSION_MINUTES: SwitchValues.FRESH_SESSION_MINUTES.default(0),
  MIC_GATE_MODE: SwitchValues.MIC_GATE_MODE.default("attenuate"),
  VAD_PROFILE: SwitchValues.VAD_PROFILE.default("home"),
  DUCK_ENABLED: SwitchValues.DUCK_ENABLED.default(false),
  /** Raises per-IP limits, the daily ceiling and the daily spend cap together (design 9.4). */
  EVENT_MODE: SwitchValues.EVENT_MODE.default(false),
  HYBRID_VAD_SHARE: SwitchValues.HYBRID_VAD_SHARE.default(0),
  /** Share of real consults the quality judge scores. 0 until the judge ships. */
  JUDGE_SAMPLE_RATE: SwitchValues.JUDGE_SAMPLE_RATE.default(0),
  RESUME_EMAIL_ENABLED: SwitchValues.RESUME_EMAIL_ENABLED.default(false),
  VOICE_ENGINE: SwitchValues.VOICE_ENGINE.default("live"),
  CASCADE_SHARE: SwitchValues.CASCADE_SHARE.default(0),
});
export type Switches = z.infer<typeof Switches>;
export type SwitchKey = keyof Switches;
export const SWITCH_KEYS = Object.keys(Switches.shape) as SwitchKey[];

/**
 * A patch of switches: only the keys present are changed. No defaults are
 * filled in, and unknown keys are refused.
 */
export const SwitchesPatch = z.strictObject({
  CONSULTS_ENABLED: SwitchValues.CONSULTS_ENABLED.optional(),
  DRAIN: SwitchValues.DRAIN.optional(),
  ROUTE_TO_LEGACY: SwitchValues.ROUTE_TO_LEGACY.optional(),
  HANDOFF_ENABLED: SwitchValues.HANDOFF_ENABLED.optional(),
  CRM_ENABLED: SwitchValues.CRM_ENABLED.optional(),
  SLACK_ENABLED: SwitchValues.SLACK_ENABLED.optional(),
  COACH_ENABLED: SwitchValues.COACH_ENABLED.optional(),
  CHECKPOINT_ENABLED: SwitchValues.CHECKPOINT_ENABLED.optional(),
  DRAFTER_MODEL: SwitchValues.DRAFTER_MODEL.optional(),
  WARM_START_ENABLED: SwitchValues.WARM_START_ENABLED.optional(),
  SPANISH_ENABLED: SwitchValues.SPANISH_ENABLED.optional(),
  VIDEO_MODE_ENABLED: SwitchValues.VIDEO_MODE_ENABLED.optional(),
  VOICE_MODE_ENABLED: SwitchValues.VOICE_MODE_ENABLED.optional(),
  CHANNELS_ACTIVE: SwitchValues.CHANNELS_ACTIVE.optional(),
  VOICE_MODEL: SwitchValues.VOICE_MODEL.optional(),
  FRESH_SESSION_MINUTES: SwitchValues.FRESH_SESSION_MINUTES.optional(),
  MIC_GATE_MODE: SwitchValues.MIC_GATE_MODE.optional(),
  VAD_PROFILE: SwitchValues.VAD_PROFILE.optional(),
  DUCK_ENABLED: SwitchValues.DUCK_ENABLED.optional(),
  EVENT_MODE: SwitchValues.EVENT_MODE.optional(),
  HYBRID_VAD_SHARE: SwitchValues.HYBRID_VAD_SHARE.optional(),
  JUDGE_SAMPLE_RATE: SwitchValues.JUDGE_SAMPLE_RATE.optional(),
  RESUME_EMAIL_ENABLED: SwitchValues.RESUME_EMAIL_ENABLED.optional(),
  VOICE_ENGINE: SwitchValues.VOICE_ENGINE.optional(),
  CASCADE_SHARE: SwitchValues.CASCADE_SHARE.optional(),
});
export type SwitchesPatch = z.infer<typeof SwitchesPatch>;

/** Every switch at its default. */
export function defaultSwitches(): Switches {
  return Switches.parse({});
}

/**
 * Turn a raw string (env var or D1 settings value) into the switch's type.
 * D1 stores JSON text; env vars are plain strings, so both forms are read.
 */
export function parseSwitchValue(key: SwitchKey, raw: string): { ok: true; value: unknown } | { ok: false } {
  const schema = SwitchValues[key];
  const text = raw.trim();
  const candidates: unknown[] = [];
  try {
    candidates.push(JSON.parse(text));
  } catch {
    // not JSON; fall through to the plain string forms below
  }
  const lower = text.toLowerCase();
  if (["true", "on", "yes", "1"].includes(lower)) candidates.push(true);
  if (["false", "off", "no", "0"].includes(lower)) candidates.push(false);
  if (text !== "" && !Number.isNaN(Number(text))) candidates.push(Number(text));
  candidates.push(text);
  for (const candidate of candidates) {
    const result = schema.safeParse(candidate);
    if (result.success) return { ok: true, value: result.data };
  }
  return { ok: false };
}

export interface SwitchResolution {
  switches: Switches;
  /** Where each value came from. */
  source: Record<SwitchKey, "setting" | "env" | "default">;
  /** Values that did not parse and were ignored. Report these; never hide them. */
  issues: string[];
}

/**
 * Resolve every switch. `settings` is the D1 settings table as key to raw
 * text; `env` is the Worker env. Keys are the switch names in both.
 */
export function resolveSwitches(input: {
  settings?: Record<string, string | undefined>;
  env?: Record<string, unknown>;
}): SwitchResolution {
  const result = defaultSwitches() as Record<SwitchKey, unknown>;
  const source = {} as Record<SwitchKey, "setting" | "env" | "default">;
  const issues: string[] = [];
  for (const key of SWITCH_KEYS) {
    source[key] = "default";
    const envRaw = input.env?.[key];
    if (typeof envRaw === "string" && envRaw.trim() !== "") {
      const parsed = parseSwitchValue(key, envRaw);
      if (parsed.ok) {
        result[key] = parsed.value;
        source[key] = "env";
      } else issues.push(`env ${key} ignored: not a valid value`);
    }
    const settingRaw = input.settings?.[key];
    if (typeof settingRaw === "string" && settingRaw.trim() !== "") {
      const parsed = parseSwitchValue(key, settingRaw);
      if (parsed.ok) {
        result[key] = parsed.value;
        source[key] = "setting";
      } else issues.push(`setting ${key} ignored: not a valid value`);
    }
  }
  return { switches: Switches.parse(result), source, issues };
}

// Compile-time guard: the patch and the value schemas name exactly the switches.
type SameKeys<A, B> = [keyof A] extends [keyof B] ? ([keyof B] extends [keyof A] ? true : false) : false;
const patchKeysMatch: SameKeys<SwitchesPatch, Switches> = true;
const valueKeysMatch: SameKeys<typeof SwitchValues, Switches> = true;
void patchKeysMatch;
void valueKeysMatch;
